import { useCallback, useEffect, useState } from "react";
import { PIN_MAX, PIN_MIN } from "@baros/core";
import { tabletApi, type TabletSession } from "./tablet.js";

/** What an unlock hands back to the app. */
export interface Unlocked {
  readonly session: TabletSession;
  /** Where this person was when they last locked this tablet. */
  readonly resume: string | null;
}

interface Props {
  /** Drawn over a screen in use rather than on its own. */
  readonly overlay?: boolean;
  readonly onUnlocked: (u: Unlocked) => void;
  /** Sign in with a password instead: a manager setting up, or a step-up. */
  readonly onPassword: () => void;
}

const KEYS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "⌫", "0", "✓"] as const;

/**
 * An enrolled tablet's lock screen (#80): tap your name, enter your PIN.
 *
 * Names, not a PIN on its own, so a wrong guess is counted against one
 * person and the tablet rather than tried against everybody's PIN at once —
 * which is also what makes five tries a meaningful limit.
 */
export function LockScreen(p: Props) {
  const [place, setPlace] = useState<{ tablet: string; venue: string; org: string } | null>(null);
  const [staff, setStaff] = useState<readonly { userId: string; name: string }[] | null>(null);
  const [who, setWho] = useState<{ userId: string; name: string } | null>(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [info, people] = await Promise.all([tabletApi.info(), tabletApi.staff()]);
      setPlace({ tablet: info.device.name, venue: info.venueName, org: info.orgName });
      setStaff(people.staff);
    } catch (err) {
      setError(err instanceof Error ? err.message : "This tablet could not reach Bar OS");
      setStaff([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async (value: string) => {
    if (!who || value.length < PIN_MIN || busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await tabletApi.unlock(who.userId, value);
      p.onUnlocked({
        session: {
          token: res.session,
          userId: res.user.userId,
          name: res.user.name,
          idleMinutes: res.idleMinutes,
          expiresAt: res.expiresAt,
        },
        resume: res.resume,
      });
    } catch (err) {
      setPin("");
      setError(err instanceof Error ? err.message : "That did not work");
    } finally {
      setBusy(false);
    }
  };

  const press = (k: (typeof KEYS)[number]) => {
    if (k === "⌫") return setPin((v) => v.slice(0, -1));
    if (k === "✓") return void submit(pin);
    const next = (pin + k).slice(0, PIN_MAX);
    setPin(next);
  };

  // A hardware keyboard works too: digits, backspace, enter.
  useEffect(() => {
    if (!who) return;
    const onKey = (e: KeyboardEvent) => {
      if (/^\d$/.test(e.key)) setPin((v) => (v + e.key).slice(0, PIN_MAX));
      else if (e.key === "Backspace") setPin((v) => v.slice(0, -1));
      else if (e.key === "Enter") void submit(pin);
      else if (e.key === "Escape") {
        setWho(null);
        setPin("");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  return (
    <div className={`lock${p.overlay ? " lock-overlay" : ""}`} role="dialog" aria-label="Unlock this tablet">
      <div className="lock-card">
        <div className="label">{place ? `${place.org} · ${place.venue}` : "Bar OS"}</div>
        <div className="lock-title">{who ? `Hi ${who.name.split(" ")[0]}` : "Who's on?"}</div>
        <div className="label lock-sub">{place ? place.tablet : "Locked"}</div>

        {!who && (
          <div className="lock-people">
            {staff === null && <div className="label">Reading who is on this venue…</div>}
            {staff?.length === 0 && !error && (
              <div className="muted" style={{ fontSize: 14, lineHeight: 1.5 }}>
                Nobody on this venue has set a PIN yet. Sign in with a password, then set yours from the account
                panel.
              </div>
            )}
            {staff?.map((s) => (
              <button key={s.userId} className="btn lock-person" onClick={() => { setWho(s); setPin(""); setError(null); }}>
                {s.name}
              </button>
            ))}
          </div>
        )}

        {who && (
          <>
            <div className="lock-dots" aria-label={`${pin.length} digits entered`}>
              {Array.from({ length: Math.max(PIN_MIN, pin.length) }, (_, i) => (
                <span key={i} className={i < pin.length ? "on" : ""} />
              ))}
            </div>
            <div className="lock-keys">
              {KEYS.map((k) => (
                <button
                  key={k}
                  disabled={busy || (k === "✓" && pin.length < PIN_MIN)}
                  aria-label={k === "⌫" ? "Delete" : k === "✓" ? "Unlock" : k}
                  onClick={() => press(k)}
                >
                  {k}
                </button>
              ))}
            </div>
            <button className="btn sm" style={{ marginTop: 12 }} onClick={() => { setWho(null); setPin(""); setError(null); }}>
              ← Not {who.name.split(" ")[0]}
            </button>
          </>
        )}

        {error && <div className="msg err" style={{ marginTop: 12 }}>{error}</div>}

        <button className="lock-password" onClick={p.onPassword}>
          Sign in with a password instead
        </button>
      </div>
    </div>
  );
}
